import { api, ApiError } from "@/api/client";
import { getWorkspace } from "@/api/workspace";
import { activeOwner, allWork, changeWork, getWork } from "./store";
import { clone, ownerKey } from "./types";
import type { Work, Owner, Operation, Snapshot, SyncAnswer } from "./types";
import { mergeDraft, remap } from "./merge";

const running = new Set<string>();
export function validation(work: Work): string[] {
    const missing: string[] = [];
    const check = (fields: import("./types").Field[], values: import("./types").Values) => {
        for (const field of fields) {
            const value = values[field.name];
            if ((field.required || field.name === "name") && (value === false || value == null || value === "" || Array.isArray(value) && !value.length)) missing.push(field.string);
            if (field.table_ref && value && !(work.draft.tables[field.table_ref] ?? []).some(row => row.key === value)) missing.push(field.string);
        }
    };
    check(work.base.header_fields, work.draft.header);
    for (const [key, table] of Object.entries(work.base.tables)) for (const row of work.draft.tables[key] ?? []) check(table.fields, row.values);
    return [...new Set(missing)];
}
async function send(id: string) {
    if (!navigator.onLine) return;
    const owner = await activeOwner(), current = await getWork(id);
    if (!owner || !current || current.ownerKey !== ownerKey(owner) || current.base.owner.company_id !== owner.company_id
        || current.base.owner.workspace !== owner.workspace || owner.workspace !== getWorkspace()
        || !current.dirty || !current.ready || current.conflict || current.error) return;
    const missing = current.operation ? [] : validation(current);
    if (missing.length) {
        await changeWork(id, work => { if (work.operation || work.revision !== current.revision) return; work.status = "error"; work.error = missing.join("، "); }); return;
    }
    if (!current.operation) {
        await changeWork(id, work => {
            work.operation ??= { revision: work.revision, body: { schema: 1, owner: work.base.owner,
                project_id: work.base.project.id, document_id: work.base.document_id, base_version: work.base.version,
                operation_id: crypto.randomUUID(), draft: clone(work.draft) } };
            work.status = "syncing";
        });
    }
    const operation = (await getWork(id))?.operation as Operation | undefined;
    if (!operation) return;
    const active = await activeOwner();
    if (!active || ownerKey(active) !== ownerKey(owner) || active.company_id !== owner.company_id
        || active.workspace !== owner.workspace || active.workspace !== getWorkspace()) return;
    try {
        const answer = (await api.post<SyncAnswer>(`/offline/${current.base.kind}/sync`, operation.body)).data;
        if (!await getWork(id)) return;
        await changeWork(id, work => {
            if (work.operation?.body.operation_id !== operation.body.operation_id) return;
            const local = remap(work.draft, work.base.tables, answer.key_map);
            const sent = remap(operation.body.draft, work.base.tables, answer.key_map);
            work.aliases ??= {};
            for (const [table, mapping] of Object.entries(answer.key_map)) {
                work.aliases[table] = { ...work.aliases[table], ...Object.fromEntries(Object.entries(mapping).filter(([before, after]) => before !== after)) };
                for (const [before, after] of Object.entries(mapping)) {
                    const localId = work.localRows?.[table]?.[before];
                    if (localId && work.localRows?.[table]) work.localRows[table]![after] = localId;
                }
            }
            work.base = answer.snapshot;
            work.draft = work.revision === operation.revision ? clone(answer.snapshot.draft)
                : mergeDraft(sent, local, answer.snapshot.draft).draft;
            work.dirty = work.revision !== operation.revision;
            work.operation = undefined; work.status = undefined; work.error = undefined; work.savedAt = Date.now();
        });
        return true;
    } catch (error) {
        if (!await getWork(id)) return;
        const active = await activeOwner();
        if (!active || ownerKey(active) !== ownerKey(owner) || active.company_id !== owner.company_id
            || active.workspace !== owner.workspace || active.workspace !== getWorkspace()) {
            await changeWork(id, work => { if (work.operation?.body.operation_id === operation.body.operation_id) work.status = undefined; });
            return;
        }
        if (error instanceof ApiError && error.code === "offline_conflict") {
            let remote = error.snapshot as Snapshot | undefined;
            try { if (!remote) remote = (await api.raw<Snapshot>(`/offline/${current.base.kind}/snapshot?document=${operation.body.document_id}`)).data; } catch { /* Deleted or no longer readable. */ }
            await changeWork(id, work => {
                if (work.operation?.body.operation_id !== operation.body.operation_id) return;
                work.status = "conflict"; work.error = error.message; work.conflict = remote;
                // A rejected operation made no changes and can be replaced after review.
                work.operation = undefined;
            });
        } else if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
            await changeWork(id, work => { if (work.operation?.body.operation_id !== operation.body.operation_id) return; work.status = "error"; work.error = error.message; work.operation = undefined; });
        } else {
            // Unknown outcome: retain the exact operation ID and body for a safe retry.
            await changeWork(id, work => { if (work.operation?.body.operation_id === operation.body.operation_id) work.status = undefined; });
        }
    }
}
export async function syncWork(id: string) {
    if (running.has(id) || !navigator.onLine) return;
    running.add(id);
    let accepted = false;
    try {
        if (navigator.locks) accepted = Boolean(await navigator.locks.request(`pm-offline-sync-${id}`, { ifAvailable: true }, async lock => lock ? send(id) : undefined));
        else accepted = Boolean(await send(id)); // Server receipts also prevent duplicate creation across tabs.
    } finally { running.delete(id); }
    // Apply edits made during an accepted request immediately; a failed or
    // unknown request keeps its operation and waits for the next normal retry.
    if (accepted) void syncWork(id).catch(() => {});
}
export async function syncAll(owner?: Owner) {
    const active = owner ?? await activeOwner();
    if (!active || !navigator.onLine) return;
    for (const work of await allWork(active)) if (work.base.owner.company_id === active.company_id
        && work.base.owner.workspace === active.workspace && work.dirty && work.ready && !work.error && !work.conflict) await syncWork(work.id);
}
